import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Target, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const { t, language } = useLanguage();

  const values = [
    {
      icon: TrendingUp,
      title: language === 'fr' ? 'Méthode Scientifique' : 'Método Científico',
      description: language === 'fr'
        ? 'Nos grilles sont basées sur des analyses statistiques rigoureuses et des algorithmes avancés.'
        : 'Nuestras cuadrículas se basan en análisis estadísticos rigurosos y algoritmos avanzados.'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Transparence' : 'Transparencia',
      description: language === 'fr'
        ? 'Nous croyons en la transparence totale. Pas de promesses irréalistes, juste des faits et des chiffres.'
        : 'Creemos en la transparencia total. Sin promesas irreales, solo hechos y cifras.'
    },
    {
      icon: Target,
      title: language === 'fr' ? 'Simplicité' : 'Simplicidad',
      description: language === 'fr'
        ? 'Notre service est conçu pour être simple et accessible. Recevez vos grilles par email, sans complications.'
        : 'Nuestro servicio está diseñado para ser simple y accesible. Recibe tus cuadrículas por correo electrónico, sin complicaciones.'
    },
    {
      icon: Award,
      title: language === 'fr' ? 'Excellence' : 'Excelencia',
      description: language === 'fr'
        ? 'Nous nous engageons à fournir les meilleures grilles possibles, optimisées semaine après semaine.'
        : 'Nos comprometemos a proporcionar las mejores cuadrículas posibles, optimizadas semana tras semana.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {language === 'fr' ? 'À Propos de ProbaWins' : 'Acerca de ProbaWins'}
          </h1>
          <p className="text-lg md:text-xl text-gray-600">
            {language === 'fr'
              ? 'Nous révolutionnons la façon de jouer à l\'EuroMillions grâce aux statistiques et à l\'intelligence des données.'
              : 'Revolucionamos la forma de jugar a EuroMillones gracias a las estadísticas y la inteligencia de datos.'}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {language === 'fr' ? 'Notre Mission' : 'Nuestra Misión'}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {language === 'fr'
                  ? 'Chez ProbaWins, nous croyons que la chance peut être optimisée. Notre mission est de donner à chaque joueur les outils pour jouer plus intelligemment, en utilisant la puissance des statistiques et des probabilités.'
                  : 'En ProbaWins, creemos que la suerte puede optimizarse. Nuestra misión es dar a cada jugador las herramientas para jugar de manera más inteligente, utilizando el poder de las estadísticas y las probabilidades.'}
              </p>
              <p className="text-lg text-gray-600">
                {language === 'fr'
                  ? 'Fini le hasard total. Avec ProbaWins, chaque grille est le résultat d\'une analyse approfondie de milliers de tirages, pour maximiser vos chances de gagner.'
                  : 'Se acabó el azar total. Con ProbaWins, cada cuadrícula es el resultado de un análisis profundo de miles de sorteos, para maximizar tus posibilidades de ganar.'}
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwYW5hbHlzaXN8ZW58MHx8fHwxNzc5Mjk0Nzg1fDA&ixlib=rb-4.1.0&q=85"
                alt="Data Analysis"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Valeurs' : 'Nuestros Valores'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'fr'
                ? 'Les principes qui guident notre travail et notre engagement envers nos utilisateurs.'
                : 'Los principios que guían nuestro trabajo y nuestro compromiso con nuestros usuarios.'}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <Icon className="w-12 h-12 text-emerald-500 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {language === 'fr' ? 'Notre Histoire' : 'Nuestra Historia'}
            </h2>
          </div>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 mb-6">
              {language === 'fr'
                ? 'ProbaWins est né d\'une simple observation : la plupart des joueurs d\'EuroMillions choisissent leurs numéros au hasard, sans aucune stratégie. Nous nous sommes demandé : et si on pouvait faire mieux ?'
                : 'ProbaWins nació de una simple observación: la mayoría de los jugadores de EuroMillones eligen sus números al azar, sin ninguna estrategia. Nos preguntamos: ¿y si pudiéramos hacerlo mejor?'}
            </p>
            <p className="text-lg text-gray-600 mb-6">
              {language === 'fr'
                ? 'Après des mois de recherche et d\'analyse de dizaines de milliers de tirages historiques, nous avons développé un algorithme capable d\'identifier les combinaisons les plus prometteuses basées sur des modèles statistiques avancés.'
                : 'Después de meses de investigación y análisis de decenas de miles de sorteos históricos, desarrollamos un algoritmo capaz de identificar las combinaciones más prometedoras basadas en modelos estadísticos avanzados.'}
            </p>
            <p className="text-lg text-gray-600">
              {language === 'fr'
                ? 'Aujourd\'hui, des centaines de joueurs font confiance à ProbaWins chaque semaine pour optimiser leurs chances. Notre engagement reste le même : fournir les meilleures grilles possibles, basées sur la science, pas sur le hasard.'
                : 'Hoy, cientos de jugadores confían en ProbaWins cada semana para optimizar sus posibilidades. Nuestro compromiso sigue siendo el mismo: proporcionar las mejores cuadrículas posibles, basadas en la ciencia, no en el azar.'}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {language === 'fr' ? 'Prêt à jouer plus malin ?' : '¿Listo para jugar más inteligente?'}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {language === 'fr'
              ? 'Rejoignez les centaines de joueurs qui ont déjà fait le choix de l\'intelligence statistique.'
              : 'Únete a los cientos de jugadores que ya han elegido la inteligencia estadística.'}
          </p>
          <Link to="/nos-offres">
            <Button className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
              {language === 'fr' ? 'Voir les offres' : 'Ver ofertas'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;